import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../api";

/** The filename the API gave the watermarked PDF, else a fallback. */
const filenameFrom = (res, fallback) => {
  const header = res.headers?.["content-disposition"] || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : fallback;
};

/**
 * The PDFs of one bought PDF pack, from the dashboard's "Practice Papers (PDF packs)" list.
 *
 * Packs have no in-portal page — they are files — so this reads the same `downloads` list My
 * Purchases shows (PurchaseDownloadService::decorate) and streams the watermarked copies.
 * Answer files still need the parent PIN; the API refuses them without the grant.
 */
export default function PackDownloadsDialog({ purchaseId, title, onClose }) {
  const [loading, setLoading] = useState(true);
  const [downloads, setDownloads] = useState([]);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    if (!purchaseId) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .get("student/paper/my-purchases", { params: { only: "paper" } })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        const row = rows.find(
          (r) => r.type === "bundle" && String(r.purchase_id) === String(purchaseId)
        );
        setDownloads(Array.isArray(row?.downloads) ? row.downloads : []);
      })
      .catch(() => {
        if (!cancelled) setDownloads([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [purchaseId]);

  const download = async (item) => {
    setBusyKey(item.key);
    try {
      const res = await api.get(
        `student/purchases/bundle/${purchaseId}/download/${encodeURIComponent(item.key)}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFrom(res, `${item.label || "practice-paper"}.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(
        status === 403
          ? "Answers need your parent's PIN — download them from My Purchases."
          : "Could not download this file. Please try again."
      );
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Dialog open={Boolean(purchaseId)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>{title || "Practice Papers (PDF pack)"}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : downloads.length === 0 ? (
          <Typography color="text.secondary">
            The files for this pack are not ready yet. They will appear here once they are published.
          </Typography>
        ) : (
          <List dense disablePadding>
            {downloads.map((item) => (
              <ListItem
                key={item.key}
                disableGutters
                secondaryAction={
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={busyKey === item.key ? <CircularProgress size={14} /> : <Download />}
                    disabled={busyKey !== null}
                    onClick={() => download(item)}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Download
                  </Button>
                }
              >
                <ListItemText
                  primary={item.label}
                  secondary={[item.group, item.subject].filter(Boolean).join(" · ") || null}
                  sx={{ pr: 14 }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
